import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { MultiSelect } from '@/components/ui/multi-select'
import {
	VOLUME_OPTIONS,
	PROCESSOR_OPTIONS,
	PLATFORM_OPTIONS,
} from '../constants'
import { stepAboutBusinessSchema } from '../schemas/step-about-business'
import type { StepAboutBusinessData } from '../types'

interface StepAboutBusinessProps {
	defaultValues: StepAboutBusinessData
	onNext: (data: StepAboutBusinessData) => void
	onBack: () => void
	disabled?: boolean
}

const processorOptions = PROCESSOR_OPTIONS.map((o) => ({
	label: o.label,
	value: o.value,
}))

const platformOptions = PLATFORM_OPTIONS.map((o) => ({
	label: o.label,
	value: o.value,
}))

export function StepAboutBusiness({
	defaultValues,
	onNext,
	onBack,
	disabled = false,
}: StepAboutBusinessProps) {
	const { t } = useTranslation('auth')

	const form = useForm({
		resolver: zodResolver(stepAboutBusinessSchema),
		defaultValues,
		mode: 'onTouched',
	})

	const handleSubmit = form.handleSubmit((values) => {
		onNext(values as StepAboutBusinessData)
	})

	// Skip bypasses the form entirely, forwarding whatever's been entered so
	// far (possibly the empty defaults). No field is required, so there's
	// nothing to validate.
	const handleSkip = () => {
		onNext(form.getValues() as StepAboutBusinessData)
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-5">
			<div className="space-y-1 text-center">
				<h2 className="text-foreground text-2xl font-bold tracking-tight">
					{t('signUpNew.step2.title')}
				</h2>
				<p className="text-muted-foreground text-sm">
					{t('signUpNew.step2.subtitle')}
				</p>
				<p className="text-muted-foreground/80 text-xs">
					{t('signUpNew.step2.optionalHint')}
				</p>
			</div>

			{/* Site live toggle */}
			<Controller
				control={form.control}
				name="siteLive"
				render={({ field }) => (
					<div className="flex items-center justify-between rounded-lg border p-3">
						<Label htmlFor="siteLive" className="cursor-pointer">
							{t('signUpNew.step2.siteLive')}
						</Label>
						<Switch
							id="siteLive"
							checked={field.value}
							onCheckedChange={field.onChange}
							disabled={disabled}
						/>
					</div>
				)}
			/>

			{/* Monthly volume */}
			<div className="space-y-1.5">
				<Label>{t('signUpNew.step2.monthlyVolume')}</Label>
				<Controller
					control={form.control}
					name="monthlyVolume"
					render={({ field }) => (
						<Select
							value={field.value}
							onValueChange={field.onChange}
							disabled={disabled}
						>
							<SelectTrigger className="w-full">
								<SelectValue placeholder={t('signUpNew.step2.volumePlaceholder')} />
							</SelectTrigger>
							<SelectContent>
								{VOLUME_OPTIONS.map((opt) => (
									<SelectItem key={opt.value} value={opt.value}>
										{opt.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				/>
				{form.formState.errors.monthlyVolume && (
					<p className="text-destructive text-sm">
						{form.formState.errors.monthlyVolume.message}
					</p>
				)}
			</div>

			{/* Needs processing toggle */}
			<Controller
				control={form.control}
				name="needsProcessing"
				render={({ field }) => (
					<div className="flex items-center justify-between rounded-lg border p-3">
						<Label htmlFor="needsProcessing" className="cursor-pointer">
							{t('signUpNew.step2.needsProcessing')}
						</Label>
						<Switch
							id="needsProcessing"
							checked={field.value}
							onCheckedChange={field.onChange}
							disabled={disabled}
						/>
					</div>
				)}
			/>

			{/* Current processors */}
			<div className="space-y-1.5">
				<Label>{t('signUpNew.step2.currentProcessors')}</Label>
				<Controller
					control={form.control}
					name="currentProcessors"
					render={({ field }) => (
						<MultiSelect
							options={processorOptions}
							selected={field.value}
							onChange={field.onChange}
							placeholder={t('signUpNew.step2.processorsPlaceholder')}
							disabled={disabled}
						/>
					)}
				/>
			</div>

			{/* Platforms */}
			<div className="space-y-1.5">
				<Label>{t('signUpNew.step2.platforms')}</Label>
				<Controller
					control={form.control}
					name="platforms"
					render={({ field }) => (
						<MultiSelect
							options={platformOptions}
							selected={field.value}
							onChange={field.onChange}
							placeholder={t('signUpNew.step2.platformsPlaceholder')}
							disabled={disabled}
						/>
					)}
				/>
			</div>

			{/* Website URL */}
			<div className="space-y-1.5">
				<Label>{t('signUpNew.step2.websiteUrl')}</Label>
				<Input
					placeholder="https://example.com"
					disabled={disabled}
					{...form.register('websiteUrl')}
				/>
			</div>

			<div className="space-y-3">
				<div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
					<Button
						type="button"
						variant="outline"
						className="w-full sm:flex-1"
						onClick={onBack}
						disabled={disabled}
					>
						{t('signUpNew.back')}
					</Button>
					<Button
						type="submit"
						className="w-full sm:flex-1"
						disabled={disabled}
					>
						{t('signUpNew.continue')}
					</Button>
				</div>
				<button
					type="button"
					onClick={handleSkip}
					disabled={disabled}
					className="text-muted-foreground hover:text-foreground mx-auto block text-sm underline-offset-4 transition hover:underline disabled:opacity-50"
				>
					{t('signUpNew.step2.skip')}
				</button>
			</div>
		</form>
	)
}
